<template>
    <div>
        <div
            v-for="(question, idx) in aiQuestions"
            :key="idx"
            class="survey-ai"
        >
            <div class="survey-ai--say">
                <div class="survey-ai--say--avatar">
                    <img
                        v-if="avatarSrc && currentAiLoopIdx === idx"
                        :src="avatarSrc"
                        alt=""
                        role="presentation"
                    />
                </div>
                <div
                    class="survey-ai--say--bubble"
                    :class="{ active: currentAiLoopIdx === idx }"
                >
                    <template v-if="isLoading && currentAiLoopIdx === idx">
                        <span data-testid="loadingDot" class="dot dot-1"></span>
                        <span data-testid="loadingDot" class="dot dot-2"></span>
                        <span data-testid="loadingDot" class="dot dot-3"></span>
                    </template>
                    <template v-else>
                        <span>{{ question }}</span>
                    </template>
                </div>
            </div>
            <div class="survey-ai--receive">
                <div v-if="!aiAnswers[idx] && !isLoading">
                    <textarea
                        v-model="currentAiAnswer"
                        v-autoexpand-height
                        autofocus
                        aria-label="Your answer to the question"
                        class="survey-ai--receive--input"
                        @keypress="onKeyPress"
                    />
                    <button
                        type="button"
                        class="survey-ai--receive--button"
                        aria-label="Submit Answer"
                        :disabled="!currentAiAnswer"
                        @click="submitReplyText"
                    >
                        <img
                            src="../../../../assets/img/Icons/White/plane.svg"
                            alt=""
                            role="presentation"
                        />
                    </button>
                </div>
                <div v-if="aiAnswers[idx]" class="survey-ai--receive--answer">
                    <span>{{ aiAnswers[idx] }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import type { Ref } from 'vue'
import {
    CurrentAnswer,
    SavedAiQuestion,
    SavedAiQuestions,
    Say,
    SurveyDisplayValues,
    SurveyParams,
    QUESTION_KEY_AI,
} from '@/entities/conversation'
import { getAiQuestion } from '@/api/conversation'

interface Props {
    item: Say
    surveyDisplayValues?: SurveyDisplayValues | unknown
    surveyParams: SurveyParams
    surveyQuestionsAndAnswers: CurrentAnswer[]
    avatarSrc?: string
    isActive: boolean
    isPreview: boolean
}

const props = defineProps<Props>()
const emit = defineEmits(['callNext', 'saveAiQuestion'])
const aiQuestions: Ref<string[]> = ref([])
const aiAnswers: Ref<string[]> = ref([])
const currentAiLoopIdx = ref(-1)
const currentAiAnswer = ref('')
const maxAiQuestions = computed(() => props.item.aiQuestionCount || 0)
const isFinalAiQuestion = ref(false)
const currentSurveyContext: Ref<CurrentAnswer[]> = ref([])

const isLoading = ref(false)

onMounted(() => {
    if (props.isActive) {
        currentSurveyContext.value = props?.surveyQuestionsAndAnswers || []
        getNextAiQuestion()
    } else {
        if (props.surveyDisplayValues) {
            const savedAIQuestions = props.surveyDisplayValues[
                QUESTION_KEY_AI
            ] as SavedAiQuestion
            if (savedAIQuestions) {
                aiQuestions.value = savedAIQuestions.questions
                aiAnswers.value = savedAIQuestions.answers
            }
        }
    }
})

const getNextAiQuestion = async () => {
    isLoading.value = true
    currentAiLoopIdx.value++
    aiQuestions.value.push('...')

    // immediately save answer to the current AI question
    const answerValue = aiAnswers.value[currentAiLoopIdx.value - 1]
    if (answerValue) {
        emit(
            'saveAiQuestion',
            aiQuestions.value[currentAiLoopIdx.value - 1],
            answerValue,
            currentAiLoopIdx.value
        )
    }

    if (
        aiQuestions.value.length > maxAiQuestions.value ||
        isFinalAiQuestion.value
    ) {
        aiQuestions.value.pop()
        const newValue = {
            [QUESTION_KEY_AI]: {
                questions: aiQuestions.value,
                answers: aiAnswers.value,
            },
        } as SavedAiQuestions
        emit('callNext', newValue)
        return
    }

    const currentAiLoop =
        currentAiLoopIdx.value > 0
            ? [
                  {
                      question: aiQuestions.value[currentAiLoopIdx.value - 1],
                      answer: aiAnswers.value[currentAiLoopIdx.value - 1],
                      ai_index: currentAiLoopIdx.value,
                  },
              ]
            : []

    const updatedContext: CurrentAnswer[] = [
        ...currentSurveyContext.value,
        ...currentAiLoop,
    ]
    currentSurveyContext.value = updatedContext

    const previewParams = {
        isPreview: props.isPreview,
        surveyScore: props.surveyParams.answer,
    }

    try {
        const { data } = await getAiQuestion(
            props.surveyParams.slug,
            updatedContext,
            previewParams
        )
        if (data.success) {
            isFinalAiQuestion.value = data.result.final_question
            aiQuestions.value.splice(
                aiQuestions.value.length - 1,
                1,
                data.result.question
            )
        } else {
            handleAiQuestionError(data)
            return
        }
    } catch (e) {
        handleAiQuestionError(e)
        return
    }
    isLoading.value = false
}

const handleAiQuestionError = (error) => {
    console.error(
        'Error generating AI question. Moving on with the survey.',
        error
    )
    aiQuestions.value.pop()
    emit('callNext')
}

const submitReplyText = (evt) => {
    evt.preventDefault()
    aiAnswers.value.push(currentAiAnswer.value)
    currentAiAnswer.value = ''

    getNextAiQuestion()
}

const onKeyPress = (evt) => {
    if (evt.keyCode === 13 && evt.shiftKey) {
        submitReplyText(evt)
    }
}
</script>

<style lang="less">
@import '~@/styles/rain/colour.less';

.survey-ai {
    &--say {
        display: grid;
        grid-template-columns: 40px 1fr;
        column-gap: 20px;

        &--avatar img {
            width: 40px;
            height: 40px;
            background: #ebebeb;
            border: 3px solid white;
            border-radius: 25px;
        }

        &--bubble {
            display: inline-block;
            font-family: Roboto, Arial, Helvetica, sans-serif;
            background-color: #fff;
            border-radius: 0 20px 20px 20px;
            border: 1px solid var(--brandColor);
            padding: 12px 20px;
            margin-right: 50px;
            color: var(--brandColor);
            width: fit-content;

            &.active {
                background-color: var(--brandColor);
                border: 1px solid #fff;
                color: #fff;
            }

            .dot {
                width: 10px;
                height: 10px;
                background: #fff;
                display: inline-block;
                border-radius: 10px;
                margin: 7px 5px 0px 5px;
            }
            .dot-1 {
                animation: jump 1s ease-in-out 0s infinite;
            }
            .dot-2 {
                animation: jump 1s ease-in-out 100ms infinite;
            }
            .dot-3 {
                animation: jump 1s ease-in-out 200ms infinite;
            }
        }
    }

    &--receive {
        position: relative;
        text-align: right;
        padding: 5px;

        &--input {
            padding: 12px 60px 12px 20px;
            border-radius: 20px;
            border: 1px solid #bbb;
            min-width: 250px;
            max-width: 90%;
            resize: none;
            font-family: Arial, Helvetica, sans-serif;
            color: #bbb;
            font-size: 1em;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
                display: none;
            }

            &:focus {
                outline: none;
                border: 1px solid var(--brandColor);
                color: #000;
            }

            &.red {
                border: 1px solid #ff4c3b;
            }
        }

        &--button {
            position: absolute;
            background-color: var(--brandColor);
            border: none;
            width: 32px;
            height: 32px;
            border-radius: 16px;
            z-index: 1;
            bottom: 20px;
            right: 16px;
            cursor: pointer;
            text-align: center;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: background-color 0.3s;

            img {
                width: 18px;
                margin-top: 2px;
                display: inline-block;
            }

            &:focus {
                outline: none;
            }

            &:disabled {
                background-color: #d6d6d6;
            }
        }

        &--answer {
            font-family: Roboto, Arial, Helvetica, sans-serif;
            background-color: #eee;
            border-radius: 20px 0px 20px 20px;
            padding: 12px 20px;
            margin-left: auto;
            align-self: flex-end;
            width: fit-content;
            max-width: 85%;

            span {
                white-space: pre-wrap;
            }
        }
    }
}

/* Desktop overrides */
@media only screen and (min-width: 600px) {
    .survey-ai--receive {
        &--input {
            padding: 15px 60px 15px 30px;
            border-radius: 30px;
            font-size: 1em;
            width: 400px;
        }
        &--button {
            bottom: 17px;
            right: 16px;
        }
    }
}

@keyframes jump {
    0% {
        transform: translateY(0px);
    }
    25% {
        transform: translateY(-10px);
    }
    50% {
        transform: translateY(5px);
    }
    70% {
        transform: translateY(0px);
    }
    100% {
        transform: translateY(0px);
    }
}
</style>
